import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { clientsApi } from '../api/clients'
import { analyzeApi } from '../api/analyze'
import { useAuth } from '../contexts/AuthContext'
import { ApiError } from '../lib/api'
import { scheduleLabelSP } from '../lib/format'
import { Spinner } from '../components/Spinner'

export default function Clients() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const qc = useQueryClient()
  const navigate = useNavigate()

  const { data, isLoading } = useQuery({ queryKey: ['clients'], queryFn: () => clientsApi.list() })
  const clients = data?.clients ?? []

  const deactivate = useMutation({
    mutationFn: (id: string) => clientsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  })

  const activate = useMutation({
    mutationFn: (id: string) => clientsApi.update(id, { active: true }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  })

  const analyze = useMutation({
    mutationFn: ({ id, overwrite }: { id: string; overwrite?: boolean }) =>
      analyzeApi.run(id, { overwrite }),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['reports'] })
      navigate(`/reports/${result.reportId}`)
    },
    onError: (err, vars) => {
      // 409: já existe relatório desta semana — pede confirmação antes de substituir.
      if (err instanceof ApiError && err.status === 409) {
        if (
          confirm(
            `${err.message}\n\n` +
              'Gerar de novo substitui o atual (ações, investigações e leituras serão perdidas). Continuar?'
          )
        ) {
          analyze.mutate({ id: vars.id, overwrite: true })
        }
        return
      }
      alert(err instanceof ApiError ? err.message : 'Não foi possível gerar o relatório')
    },
  })

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Clientes</h1>
        {isAdmin && (
          <Link
            to="/clients/new"
            className="rounded-md bg-brand-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            + Novo cliente
          </Link>
        )}
      </div>

      {isLoading ? (
        <Spinner />
      ) : clients.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          Nenhum cliente cadastrado.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5">Cliente</th>
                <th className="px-4 py-2.5">Gerente</th>
                <th className="px-4 py-2.5">Agendamento</th>
                <th className="px-4 py-2.5">Relatórios</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {clients.map((c) => (
                <tr key={c.id} className={c.active ? '' : 'opacity-50'}>
                  <td className="px-4 py-2.5">
                    <Link to={`/clients/${c.id}`} className="font-medium text-slate-900 hover:text-brand-600">
                      {c.name}
                    </Link>
                    {!c.active && <span className="ml-2 text-xs text-slate-400">(inativo)</span>}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{c.primaryManager?.name ?? '—'}</td>
                  <td className="px-4 py-2.5 text-slate-600">
                    {scheduleLabelSP(c.reportDayOfWeek, c.reportHour)}
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{c._count?.weeklyReports ?? 0}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => analyze.mutate({ id: c.id })}
                          disabled={analyze.isPending}
                          className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                        >
                          {analyze.isPending && analyze.variables?.id === c.id && (
                            <Spinner className="h-3 w-3 text-white" />
                          )}
                          Gerar relatório
                        </button>
                        <Link to={`/clients/${c.id}`} className="text-xs text-brand-600 hover:underline">
                          Editar
                        </Link>
                        {isAdmin && c.active && (
                          <button
                            onClick={() => {
                              if (confirm(`Desativar o cliente "${c.name}"?`)) deactivate.mutate(c.id)
                            }}
                            className="text-xs text-red-600 hover:underline"
                          >
                            Desativar
                          </button>
                        )}
                        {isAdmin && !c.active && (
                          <button
                            onClick={() => activate.mutate(c.id)}
                            disabled={activate.isPending}
                            className="text-xs text-green-600 hover:underline disabled:opacity-50"
                          >
                            Ativar
                          </button>
                        )}
                      </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
